import Keycloak from 'keycloak-js';

import { docNgonNgu, i18next, laNgonNguHopLe } from '../../i18n';
import { useAuthStore, type UserProfile } from '../../stores/auth';
import { phamViCuaVaiTro } from '../pham-vi-cong';

/**
 * Đăng nhập thật qua Keycloak cho cổng quản trị nền tảng (client `platform-admin`, ADR-0007).
 *
 * BẢN CƠ BẢN do Dev 2 dựng để test Keycloak; người làm UI/UX hoàn thiện sau. Mọi chỗ nối Keycloak nằm trong tệp
 * này: khởi tạo, ánh xạ role sang quyền của giao diện, làm mới token, đăng xuất.
 *
 * Cấu hình đọc lúc chạy từ `/config.json` (nginx sinh từ biến môi trường, web/nginx/spa.conf.template), nên một
 * image dùng được cho mọi môi trường. Không có cấu hình, ví dụ khi chạy `vite` riêng, thì giữ chế độ dữ liệu mẫu
 * của màn đăng nhập (§14.1).
 */
export interface OidcConfig {
  keycloakUrl: string;
  realm: string;
  clientId: string;
}

let keycloak: Keycloak | null = null;
let cauHinh: OidcConfig | null = null;

/** Kết quả application-initiated action vừa quay về từ Keycloak (`kc_action_status`). */
export interface KetQuaHanhDong {
  trangThai: 'success' | 'cancelled' | 'error';
  hanhDong?: string;
}
let ketQuaHanhDong: KetQuaHanhDong | null = null;

/**
 * Máy chủ trả `keycloakUrl` tương đối (`/auth`, ADR-0106): Keycloak nằm dưới `/auth` của chính domain đang mở trang,
 * nên một giá trị đúng cho cổng tenant, cổng quản trị và mọi domain thương hiệu. `keycloak-js` và các lời gọi dưới đây
 * cần URL tuyệt đối, nên ghép với origin của trang. Giá trị tuyệt đối (dev: `http://localhost:18080/auth`) giữ nguyên.
 * Bỏ `/` cuối để các đường `${keycloakUrl}/realms/...` không có hai dấu gạch.
 */
function urlTuyetDoi(keycloakUrl: string): string {
  return new URL(keycloakUrl, window.location.origin).href.replace(/\/+$/, '');
}

async function readConfig(): Promise<OidcConfig | null> {
  try {
    const res = await fetch('/config.json', { cache: 'no-store' });
    if (!res.ok) return null;
    const cfg = (await res.json()) as Partial<OidcConfig>;
    if (!cfg.keycloakUrl || !cfg.realm || !cfg.clientId) return null;
    return { keycloakUrl: urlTuyetDoi(cfg.keycloakUrl), realm: cfg.realm, clientId: cfg.clientId };
  } catch {
    return null;
  }
}

/**
 * Lý do khởi tạo đăng nhập hỏng, theo mã `loi_sso` của màn đăng nhập (`DangNhapView`). `null` là khởi tạo xong (có hay
 * không có phiên). Màn đăng nhập đọc giá trị này để báo lý do và KHÔNG tự chuyển sang Keycloak nữa: phiên SSO còn sống
 * mà bước đổi mã lấy token cứ hỏng (ví dụ edge trả 500 ở domain thương hiệu) thì tự chuyển là vòng lặp, người dùng chỉ
 * thấy màn đăng nhập trống không lời giải thích.
 */
export type LoiKhoiTaoDangNhap = 'doi_ma_that_bai' | 'access_denied' | 'chua_kiem_duoc';
let loiKhoiTao: LoiKhoiTaoDangNhap | null = null;

export function loiKhoiTaoDangNhap(): LoiKhoiTaoDangNhap | null {
  return loiKhoiTao;
}

/**
 * Trần thời gian chờ `keycloak-js` khởi tạo. Kiểm cookie bên thứ ba có hạn 10 giây của thư viện, còn `check-sso` bằng
 * iframe thì không: trang trong iframe không gửi lại thông điệp (Keycloak trả trang lỗi, edge trả 503) là ứng dụng
 * trắng mãi. Hết hạn thì vẽ ứng dụng, màn đăng nhập báo Keycloak chưa phản hồi.
 */
const TRAN_KHOI_TAO_MS = 20_000;

/** Tham số OAuth trong fragment khi vừa quay về từ Keycloak (`response_mode=fragment` mặc định của keycloak-js). */
function thamSoQuayVe(): { coMa: boolean; loi: string | null } {
  const p = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  return { coMa: p.has('code'), loi: p.get('error') };
}

/** Đã bật đăng nhập Keycloak hay đang ở chế độ dữ liệu mẫu. */
export function oidcEnabled(): boolean {
  return keycloak !== null;
}

/**
 * Role lớp 1 trong token (`realm_access.roles`, DT21 Chương 7) → **nhãn vai trò** của giao diện.
 * `roles[0]` là nhãn hiện dưới tên ở thanh điều hướng.
 *
 * Tập `roles` trả về ở đây cũng là thứ `lib/pham-vi-cong` đọc để xếp người dùng
 * vào cổng nền tảng hay cổng tenant. Thêm một vai trò **nền tảng** mới thì phải
 * khai nó ở cả hai nơi, nếu không người đó sẽ rơi nhầm sang cổng tenant.
 *
 * Nhãn đi ra dưới dạng **khoá dịch** (`vaiTro.*`) chứ không phải chữ sẵn: hàm này
 * chạy lúc nhận token, ngoài cây React, nên không gọi được `useT()`. Sidebar dịch
 * lúc vẽ, nhờ vậy đổi ngôn ngữ không phải lấy lại token (ADR-UI-0010).
 *
 * ⚠️ Hàm này **không còn quyết quyền nào**. Trước ADR-0074 nó suy `permissions`
 * từ realm role bằng một chuỗi `if/else` ngay trong trình duyệt, nên quyền của
 * nhân sự nền tảng không ai cấu hình được: thêm một người "chỉ xem gói dịch vụ"
 * là phải sửa mã và deploy lại. Giờ cả hai cổng nhận quyền từ server, do
 * `NguCanhGate` tải trước khi vẽ màn nào:
 * - cổng tenant: `GET /api/v1/users/me/context` (ADR-0034);
 * - cổng nền tảng: `GET /api/v1/platform-context` (ADR-0074 mục 6).
 */
function toUserProfile(token: Record<string, unknown>): UserProfile {
  const realmRoles = ((token.realm_access as { roles?: string[] } | undefined)?.roles ?? []);
  const fullName =
    [token.given_name, token.family_name].filter(Boolean).join(' ') ||
    (token.name as string | undefined) ||
    (token.email as string | undefined) ||
    '';

  // Chỉ nhãn hiển thị, không phải quyền. Thứ tự xét theo mức bao trùm, như trước.
  let roles: string[] = [];
  if (realmRoles.includes('PLATFORM_ADMIN')) {
    roles = ['vaiTro.quanTriNenTang'];
  } else if (realmRoles.includes('AUDITOR')) {
    roles = ['vaiTro.kiemToan'];
  } else if (realmRoles.includes('VENDOR')) {
    roles = ['vaiTro.doiTacPhatTrienModule'];
  } else if (realmRoles.includes('TENANT_ADMIN')) {
    // Cổng tenant: không có vai trò nền tảng nào, nên `lib/pham-vi-cong` xếp
    // tài khoản này vào phạm vi `tenant` và nhóm quản trị nền tảng biến mất.
    roles = ['vaiTro.quanTriToChuc'];
  } else if (realmRoles.includes('TENANT_USER')) {
    roles = ['vaiTro.nguoiDungToChuc'];
  }

  // Role license (ADR-0058) là nhân sự nền tảng, nhưng không trùng ba vai trò ở
  // trên. Không có vai trò nền tảng nào khác thì vẫn phải vào cổng nền tảng, nên
  // gắn nhãn `vaiTro.phuTrachLicense` (khai ở `lib/pham-vi-cong`).
  if (roles.length === 0 && realmRoles.some((r) => r.startsWith('LICENSE_'))) {
    roles = ['vaiTro.phuTrachLicense'];
  }

  const laCongTenant = phamViCuaVaiTro(realmRoles) === 'tenant';

  return {
    id: token.sub as string,
    fullName,
    email: (token.email as string | undefined) ?? '',
    // ADR-0101: tên đăng nhập là preferred_username, có thể khác email nhận thư
    tenDangNhap: (token.preferred_username as string | undefined) ?? undefined,
    roles,
    // Rỗng có chủ ý: `NguCanhGate` ghi quyền thật vào kho trước khi vẽ màn nào.
    permissions: [],
    vaiTroLop1: realmRoles,
    ...(laCongTenant ? { dichVu: [] } : {}),
  };
}

/**
 * Gọi một lần trước khi vẽ ứng dụng. Có phiên Keycloak thì nạp người dùng vào kho; quay về từ trang đăng nhập
 * (`?code=`) thì keycloak-js tự đổi code lấy token (PKCE S256).
 */
/**
 * Kiểm phiên còn sống ở Keycloak. Access token sống 5 phút và cổng chỉ làm mới khi token hết hạn, nên phiên bị đăng
 * xuất từ thiết bị khác (màn "Tài khoản của tôi", Tenant Admin, khoá phiên ADR-0016) vẫn dùng được tới lúc đó. Buộc làm
 * mới token định kỳ và mỗi khi người dùng quay lại tab: refresh token của phiên đã huỷ bị Keycloak từ chối.
 */
const CHU_KY_KIEM_PHIEN_MS = 60_000;

function theoDoiPhien(): void {
  window.setInterval(kiemPhienNgay, CHU_KY_KIEM_PHIEN_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') kiemPhienNgay();
  });
}

/**
 * Kiểm phiên **ngay bây giờ**, cùng cách kiểm định kỳ ở trên: buộc làm mới
 * token, bị từ chối thì đi qua `xuLyKhongLamMoiDuoc` (phân biệt phiên đã huỷ với
 * Keycloak tạm không phản hồi, rồi mới chuyển trang).
 *
 * Kênh sự kiện (`lib/api/su-kien.ts`) gọi hàm này khi máy chủ báo phiên vừa
 * đổi, bị khoá, hoặc luồng nhận 401 — để phiên bị huỷ từ thiết bị khác thoát
 * ra trong vài giây thay vì chờ tới nhịp kiểm định kỳ. Không có Keycloak (dữ
 * liệu mẫu), chưa đăng nhập, hoặc đang trên đường kết thúc phiên thì không làm
 * gì.
 */
export function kiemPhienNgay(): void {
  const kc = keycloak;
  const cfg = cauHinh;
  if (!kc || !cfg || !kc.authenticated || dangKetThucPhien) return;
  kc.updateToken(-1).catch(() => void xuLyKhongLamMoiDuoc(kc, cfg, duongDanHienTai()));
}

/**
 * Làm mới token thất bại có hai nguyên nhân: phiên đã bị huỷ, hoặc Keycloak tạm không phản hồi. keycloak-js không phân
 * biệt hai ca, nên hỏi thử tài liệu cấu hình của realm: Keycloak trả lời được tức phiên đã huỷ, đưa về màn đăng nhập kèm
 * lý do; không trả lời thì giữ nguyên, lần kiểm sau thử lại, để người dùng không bị đá ra chỉ vì mạng chập chờn.
 */
function duongDanHienTai(): string {
  return `${window.location.pathname}${window.location.search}`;
}

let dangKetThucPhien = false;

async function xuLyKhongLamMoiDuoc(kc: Keycloak, cfg: OidcConfig, veLai: string): Promise<void> {
  if (dangKetThucPhien) return;
  try {
    const res = await fetch(`${cfg.keycloakUrl}/realms/${cfg.realm}/.well-known/openid-configuration`, {
      cache: 'no-store',
    });
    if (!res.ok) return;
  } catch {
    return;
  }
  dangKetThucPhien = true;
  // Chuyển trang trước khi xoá người dùng, để AuthGate không kịp vẽ /dang-nhap rồi tự mở trang Keycloak
  window.location.replace(`/dang-nhap?loi_sso=phien_ket_thuc&redirect=${encodeURIComponent(veLai)}`);
  kc.clearToken();
  useAuthStore.setState({ user: null });
}

export async function initAuth(): Promise<void> {
  const cfg = await readConfig();
  if (!cfg) return;

  const kc = new Keycloak({ url: cfg.keycloakUrl, realm: cfg.realm, clientId: cfg.clientId });
  keycloak = kc;
  cauHinh = cfg;

  // Phải gắn **trước** `init()`: keycloak-js gọi hàm này ngay lúc đọc callback
  // có `kc_action_status`, tức khi quay về từ trang cài OTP hay đổi mật khẩu.
  kc.onActionUpdate = (trangThai, hanhDong) => {
    ketQuaHanhDong = { trangThai, hanhDong };
  };

  // Đăng xuất của giao diện kết thúc cả phiên Keycloak của cổng này, rồi quay về màn đăng nhập.
  //
  // `dangKetThucPhien` phải bật TRƯỚC khi chuyển trang: nhịp kiểm phiên định kỳ (mỗi 60 giây) và
  // `visibilitychange` vẫn chạy trong lúc trình duyệt đang rời trang. Thiếu cờ này thì một nhịp
  // kiểm chen vào giữa, `check-sso` thấy phiên Keycloak chưa kịp đóng và nạp lại người dùng —
  // bấm Đăng xuất ở màn chặn xong lại quay về đúng màn chặn đó, không thoát ra được.
  useAuthStore.setState({
    logout: () => {
      dangKetThucPhien = true;
      // `logout()` dựng URL đăng xuất ngay khi gọi, đọc `kc.idToken` để gắn `id_token_hint`: thiếu nó Keycloak hiện
      // trang "Bạn có muốn đăng xuất không?" bắt bấm thêm một lần.
      //
      // KHÔNG xoá token hay `user` ở đây: trang sắp rời sang Keycloak, cứ giữ nguyên màn đang đứng. Xoá `user` làm
      // AuthGate chuyển ngay sang `/dang-nhap?redirect=…` (không có `da_dang_xuat`), màn đăng nhập tự gọi `login()` và
      // lệnh chuyển trang đó đè lên lệnh đăng xuất: phiên Keycloak còn sống hoặc được mở lại, người dùng về đúng màn
      // vừa đứng và phải bấm Đăng xuất lần hai (GG-FX-15).
      void kc.logout({ redirectUri: `${window.location.origin}/dang-nhap?da_dang_xuat=1` });
    },
  });

  kc.onTokenExpired = () => {
    kc.updateToken(30).catch(() => void xuLyKhongLamMoiDuoc(kc, cfg, duongDanHienTai()));
  };
  // keycloak-js gọi hàm này khi làm mới token bị từ chối (phiên đã huỷ), trước cả nhánh catch của updateToken. Đọc
  // đường dẫn ngay lúc này, khi router chưa đổi trang.
  kc.onAuthLogout = () => void xuLyKhongLamMoiDuoc(kc, cfg, duongDanHienTai());

  // Đọc trước init(): keycloak-js xoá fragment khỏi thanh địa chỉ ngay khi xử lý
  const quayVe = thamSoQuayVe();
  let hetGio: number | undefined;
  try {
    const authenticated = await Promise.race([
      kc.init({
        onLoad: 'check-sso',
        pkceMethod: 'S256',
        checkLoginIframe: false,
        silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
      }),
      new Promise<never>((_, reject) => {
        hetGio = window.setTimeout(() => reject(new Error('keycloak-js init timeout')), TRAN_KHOI_TAO_MS);
      }),
    ]);
    if (authenticated && kc.tokenParsed) {
      useAuthStore.setState({ user: toUserProfile(kc.tokenParsed as Record<string, unknown>) });
      theoDoiPhien();
    }
  } catch (err) {
    // Vẫn vẽ ứng dụng; màn đăng nhập báo lý do thay vì tự chuyển lại sang Keycloak (vòng lặp)
    loiKhoiTao =
      quayVe.loi === 'access_denied' ? 'access_denied' : quayVe.coMa || quayVe.loi ? 'doi_ma_that_bai' : 'chua_kiem_duoc';
    console.error(`Keycloak init failed (${loiKhoiTao}) at ${cfg.keycloakUrl}`, err);
  } finally {
    window.clearTimeout(hetGio);
  }
}

/** Chuyển sang trang đăng nhập chung của Keycloak; xong quay lại `redirect` của ứng dụng. */
export function login(redirect: string): Promise<void> {
  if (!keycloak) return Promise.reject(new Error('Keycloak is not configured'));
  const target = redirect.startsWith('/') && !redirect.startsWith('//') ? redirect : '/';
  // Trang đăng nhập chung hiện cùng ngôn ngữ với cổng (`ui_locales`); thiếu tham số này Keycloak chọn theo
  // Accept-Language của trình duyệt, nên cổng đang tiếng Việt mà trang đăng nhập ra tiếng Anh.
  return keycloak.login({ redirectUri: `${window.location.origin}${target}`, locale: ngonNguHienTai() });
}

/** Access token còn hạn ít nhất 30 giây, để gắn `Authorization: Bearer` khi gọi API qua Gateway. */
export async function getAccessToken(): Promise<string | undefined> {
  if (!keycloak?.authenticated) return undefined;
  await keycloak.updateToken(30);
  return keycloak.token;
}

/** Ngôn ngữ đang dùng của cổng, gửi sang Keycloak qua `ui_locales`. */
function ngonNguHienTai(): string {
  return laNgonNguHopLe(i18next.language) ? i18next.language : docNgonNgu();
}

/**
 * Mở một application-initiated action của Keycloak (ADR-0027, ADR-0028 mục 2):
 * `CONFIGURE_TOTP`, `UPDATE_PASSWORD`, `delete_credential:<id>`. Keycloak hiện
 * đúng một bước đó bằng theme `platform`, xong quay về `quayVe` của ứng dụng.
 */
export function moHanhDongKeycloak(hanhDong: string, quayVe: string): Promise<void> {
  if (!keycloak) return Promise.reject(new Error('Keycloak is not configured'));
  const target = quayVe.startsWith('/') && !quayVe.startsWith('//') ? quayVe : '/';
  return keycloak.login({
    action: hanhDong,
    redirectUri: `${window.location.origin}${target}`,
    locale: ngonNguHienTai(),
  });
}

/** Khoá `sessionStorage` ghi lúc vừa xin xác thực tăng cường, để nhận ra vòng lặp (ADR-0090 mục 2). */
const KHOA_BUOC_TANG = 'platform.buoc-tang-luc';
/** Trong khoảng này sau khi xin mà Gateway vẫn đòi OTP thì coi là không xác thực tăng cường được. */
const CUA_SO_BUOC_TANG_MS = 3 * 60_000;

/**
 * Xác thực tăng cường (step-up, ADR-0090 mục 2): Gateway trả `401 CAN_XAC_THUC_TANG_CUONG` khi người dùng ở ngoài
 * dải tin cậy, tổ chức đòi OTP mà phiên chưa qua OTP. Chuyển sang Keycloak với `acr_values=otp`: đang có phiên SSO thì
 * Keycloak không hỏi lại mật khẩu, chỉ hỏi mã OTP (hoặc hướng dẫn cài OTP nếu chưa cài), rồi quay về `quayVe` với
 * token mang `acr = otp`.
 */
export function xacThucTangCuong(quayVe: string): Promise<void> {
  if (!keycloak) return Promise.reject(new Error('Keycloak is not configured'));
  const target = quayVe.startsWith('/') && !quayVe.startsWith('//') ? quayVe : '/';
  try {
    window.sessionStorage.setItem(KHOA_BUOC_TANG, String(Date.now()));
  } catch {
    // Trình duyệt chặn sessionStorage: chỉ mất phần nhận ra vòng lặp
  }
  return keycloak.login({
    acrValues: 'otp',
    redirectUri: `${window.location.origin}${target}`,
    locale: ngonNguHienTai(),
  });
}

/**
 * Vừa xác thực tăng cường xong (trong vài phút) mà Gateway vẫn đòi: realm chưa cấu hình mức xác thực, hoặc người dùng
 * huỷ bước OTP. Màn chặn đổi câu để không mời bấm lại mãi.
 */
export function vuaXacThucTangCuong(bayGio = Date.now()): boolean {
  try {
    const luc = Number(window.sessionStorage.getItem(KHOA_BUOC_TANG));
    return Number.isFinite(luc) && luc > 0 && bayGio - luc < CUA_SO_BUOC_TANG_MS;
  } catch {
    return false;
  }
}

/**
 * Lấy **một lần** kết quả của action vừa quay về; lần gọi sau trả `null`, để
 * thông báo không hiện lại khi người dùng rời màn rồi quay lại.
 */
export function layKetQuaHanhDong(): KetQuaHanhDong | null {
  const kq = ketQuaHanhDong;
  ketQuaHanhDong = null;
  return kq;
}

/** Gốc Account REST API của realm: `{keycloak}/realms/{realm}/account`. */
export function duongDanAccountApi(): string | undefined {
  if (!cauHinh) return undefined;
  return `${cauHinh.keycloakUrl}/realms/${cauHinh.realm}/account`;
}

/**
 * Tenant của người này bật MFA bắt buộc (ADR-0015): group `/tenants/{slug}`
 * mang client role `platform-policy/mfa-required`, và token của hai cổng mang
 * role đó ở `resource_access` vì hai client để `fullScopeAllowed` mặc định.
 */
export function batBuocMfaTheoToken(): boolean {
  const ra = keycloak?.tokenParsed?.resource_access as
    | Record<string, { roles?: string[] }>
    | undefined;
  return ra?.['platform-policy']?.roles?.includes('mfa-required') ?? false;
}
